import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown, Check, Loader2, AlertTriangle, MapPin, Users, Gauge } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TheaterSelector = ({ token, activeTheater, onTheaterChange, onBriefChange }) => {
  const [theaters, setTheaters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTheater, setSelectedTheater] = useState(null);

  useEffect(() => {
    fetchTheaters();
  }, [token]);

  useEffect(() => {
    if (activeTheater && theaters.length > 0) {
      const theater = theaters.find(t => t.theater_id === activeTheater);
      if (theater) {
        setSelectedTheater(theater);
      }
    }
  }, [activeTheater, theaters]);

  const fetchTheaters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/theaters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTheaters(response.data.theaters || []);
      
      // Sélectionner Ormuz par défaut
      if (!activeTheater && response.data.theaters?.length > 0) {
        const hormuz = response.data.theaters.find(t => t.theater_id === 'hormuz');
        if (hormuz) {
          setSelectedTheater(hormuz);
          onTheaterChange?.(hormuz.theater_id);
        }
      }
    } catch (err) {
      setError('Erreur de chargement des théâtres');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTheater = async (theater) => {
    setSelectedTheater(theater);
    setIsOpen(false);
    onTheaterChange?.(theater.theater_id);
    
    // Charger les détails du théâtre pour le brief
    try {
      const response = await axios.get(`${API_URL}/api/theater/${theater.theater_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const details = response.data.theater;
      if (details?.default_brief && onBriefChange) {
        onBriefChange(details.default_brief);
      }
    } catch (err) {
      console.error('Error loading theater details:', err);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'energy': return 'text-amber-400 bg-amber-400/10';
      case 'geopolitical': return 'text-cyan-400 bg-cyan-400/10';
      case 'maritime': return 'text-blue-400 bg-blue-400/10';
      case 'regional': return 'text-slate-400 bg-slate-400/10';
      default: return 'text-slate-400 bg-slate-400/10';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'energy': return 'ÉNERGIE';
      case 'geopolitical': return 'GÉOPOLITIQUE';
      case 'maritime': return 'MARITIME';
      case 'regional': return 'RÉGIONAL';
      default: return 'AUTRE';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 p-3 bg-slate-800/50 rounded-lg border border-slate-600/30">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="font-mono text-sm">CHARGEMENT DES THÉÂTRES...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-400 p-3 bg-red-500/10 rounded-lg border border-red-500/30">
        <AlertTriangle className="w-4 h-4" />
        <span className="font-mono text-sm">{error}</span>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="theater-selector">
      {/* Bouton principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-cyan-500/30 hover:border-cyan-500/50 transition-colors"
        data-testid="theater-selector-btn"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{selectedTheater?.icon || '🌍'}</span>
          <div className="text-left">
            <div className="font-mono text-xs text-slate-400">THÉÂTRE D'ANALYSE</div>
            <div className="font-bold text-white">
              {selectedTheater?.name_short || 'Sélectionner un théâtre'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedTheater && (
            <span className={`px-2 py-0.5 rounded text-xs font-mono ${getCategoryColor(selectedTheater.category)}`}>
              {getCategoryLabel(selectedTheater.category)}
            </span>
          )}
          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-2 bg-slate-800 border border-cyan-500/30 rounded-lg shadow-xl overflow-hidden"
          >
            <div className="max-h-96 overflow-y-auto">
              {theaters.map((theater) => (
                <button
                  key={theater.theater_id}
                  onClick={() => handleSelectTheater(theater)}
                  className={`w-full p-3 flex items-start gap-3 hover:bg-slate-700/50 transition-colors border-b border-slate-700/50 last:border-0 ${
                    selectedTheater?.theater_id === theater.theater_id ? 'bg-cyan-500/10' : ''
                  }`}
                  data-testid={`theater-option-${theater.theater_id}`}
                >
                  <span className="text-2xl">{theater.icon}</span>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white">{theater.name_short}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-mono ${getCategoryColor(theater.category)}`}>
                        {getCategoryLabel(theater.category)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 mt-1 line-clamp-2">
                      {theater.description}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {theater.actors?.length || 0} acteurs
                      </span>
                      <span className="flex items-center gap-1">
                        <Gauge className="w-3 h-3" />
                        {theater.active_modules?.length || 0} modules
                      </span>
                    </div>
                  </div>
                  {selectedTheater?.theater_id === theater.theater_id && (
                    <Check className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop pour fermer le dropdown */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default TheaterSelector;
